import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import { useState } from 'react';
import {
  useCreateBusinessUnitHolidayMutation,
  useDeleteBusinessUnitHolidayMutation,
  useGetBusinessUnitHolidaysQuery,
} from 'services/api/businessUnits.api';

interface Props {
  businessUnitId: number;
}

const HolidaysSection: React.FC<Props> = ({ businessUnitId }) => {
  const { data: holidays = [], isLoading } = useGetBusinessUnitHolidaysQuery({ businessUnitId });
  const [createHoliday, { isLoading: isCreating }] = useCreateBusinessUnitHolidayMutation();
  const [deleteHoliday] = useDeleteBusinessUnitHolidayMutation();

  const [date, setDate] = useState('');
  const [name, setName] = useState('');

  const canSubmit = /^\d{4}-\d{2}-\d{2}$/.test(date) && name.trim().length > 0 && !isCreating;

  const handleAdd = async () => {
    if (!canSubmit) return;
    await createHoliday({ businessUnitId, date, name: name.trim() }).unwrap();
    setDate('');
    setName('');
  };

  const handleDelete = async (holidayId: number) => {
    await deleteHoliday({ businessUnitId, holidayId }).unwrap();
  };

  return (
    <Box sx={{ my: 3 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }} variant="subtitle2">
        Feriados — sucursales cerradas
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        Días en que las sucursales no operan. La IA avisará a los clientes.
      </Typography>

      {isLoading ? (
        <Typography color="text.secondary" sx={{ mb: 3 }} variant="body2">
          Cargando…
        </Typography>
      ) : holidays.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 3 }} variant="body2">
          No hay feriados configurados.
        </Typography>
      ) : (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', mb: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}
        >
          {holidays.map((holiday, idx) => (
            <Box
              key={holiday.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.25,
                backgroundColor: idx % 2 === 0 ? 'action.hover' : 'transparent',
                borderBottom: idx < holidays.length - 1 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontWeight: 600, minWidth: 110 }} variant="body2">
                {holiday.date}
              </Typography>
              <Typography sx={{ flex: 1 }} variant="body2">
                {holiday.name}
              </Typography>
              <IconButton aria-label={`Eliminar ${holiday.name}`} onClick={() => handleDelete(holiday.id)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          label="Fecha"
          onChange={(event) => setDate(event.target.value)}
          size="small"
          sx={{ minWidth: 170 }}
          type="date"
          value={date}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Nombre del feriado"
          onChange={(event) => setName(event.target.value)}
          placeholder="Año Nuevo"
          size="small"
          sx={{ minWidth: 240, flex: 1 }}
          value={name}
        />
        <PrimaryBtn disabled={!canSubmit} onClick={handleAdd} variant="contained">
          Agregar
        </PrimaryBtn>
      </Box>
    </Box>
  );
};

export default HolidaysSection;
